import * as React from 'react'
import Button from '@mui/material/Button'
import ButtonGroup from '@mui/material/ButtonGroup'
import { ChevronDown, Loader } from 'react-feather'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import Grow from '@mui/material/Grow'
import Paper from '@mui/material/Paper'
import Popper, { PopperPlacementType } from '@mui/material/Popper'
import MenuItem from '@mui/material/MenuItem'
import MenuList from '@mui/material/MenuList'
import { makeStyles, withStyles } from '@material-ui/core/styles'
import { Tooltip, Typography, useTheme } from '@mui/material'
import { useEffect } from 'react'
import Permission from 'src/services/PermissionService'
import { CaretDown } from '@phosphor-icons/react'

interface DropdownButtonProps {
  options: any
  handleClick: (index: number, value: string) => void
  variant?: 'contained' | 'outlined'
  title?: React.ReactNode
  selectedOption?: number
  size?: 'small' | 'large' | 'minimal'
  fullWidth?: boolean
  renderOnlyIcon?: boolean
  disabled?: boolean
  tooltipTitle?: string
  disablePortal?: boolean
  popperConfig?: {
    maxHeight?: string
    placement?: PopperPlacementType
  }
  disableSelectedOption?: boolean
  hideIcon?: boolean
  hideLabel?: boolean
  // When provided, the dropdown's open state is controlled by the parent via isOpen/onOpenChange instead of being closed on every option click - used to keep the menu open while an async selection is pending
  isOpen?: boolean
  onOpenChange?: (open: boolean) => void
  isSelectionLoading?: boolean
  // Optional wrapper class for the icon/spinner slot on each option - only applied when provided, so existing consumers keep their current spacing untouched
  iconSlotClassName?: string
}

const styles = {
  root: {
    '&:disabled': {
      border: '1px solid #f0f0f0',
      backgroundColor: 'white'
    }
  }
}

const TriggerButton = withStyles(styles)(Button)

const DropdownButton: React.FC<DropdownButtonProps> = ({
  options,
  handleClick,
  variant = 'contained',
  title,
  selectedOption,
  size = 'small',
  fullWidth = false,
  renderOnlyIcon = false,
  disabled = false,
  tooltipTitle = '',
  disablePortal = true,
  popperConfig,
  disableSelectedOption = false,
  hideIcon = false,
  hideLabel = false,
  isOpen,
  onOpenChange,
  isSelectionLoading = false,
  iconSlotClassName
}) => {
  // const classes = useStyles()
  const theme = useTheme()
  const isControlledOpen = typeof isOpen !== 'undefined'
  const [internalOpen, setInternalOpen] = React.useState(false)
  const open = isControlledOpen ? isOpen : internalOpen
  const anchorRef = React.useRef<HTMLDivElement>(null)
  const [selectedIndex, setSelectedIndex] = React.useState(0)
  const [pendingIndex, setPendingIndex] = React.useState<number | null>(null)

  const updateOpen = (next: boolean) => {
    if (isControlledOpen) {
      onOpenChange?.(next)
    } else {
      setInternalOpen(next)
    }
  }

  useEffect(() => {
    if (!isSelectionLoading) {
      setPendingIndex(null)
    }
  }, [isSelectionLoading])

  useEffect(() => {
    if (typeof selectedOption !== 'undefined') {
      setSelectedIndex(selectedOption)
    }
  }, [selectedOption])

  useEffect(() => {
    if (typeof options !== 'undefined' && options?.length) {
      !Boolean(options?.[selectedIndex]) && setSelectedIndex(0)
    }
  }, [options])

  const handleMenuItemClick = (event: React.MouseEvent<HTMLLIElement, MouseEvent>, index: number, value: string) => {
    if (isSelectionLoading) {
      return
    }

    if (isControlledOpen) {
      // Selection stays pending until the parent closes it via isOpen. selectedIndex only ever moves once the parent's selectedOption prop reflects the confirmed value - never
      // optimistically here.
      setPendingIndex(index)
      handleClick(index, value)
      return
    }

    if (!disableSelectedOption) {
      setSelectedIndex(index)
    }
    handleClick(index, value)
    updateOpen(false)
  }

  const handleToggle = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    if (isSelectionLoading) {
      return
    }
    updateOpen(!open)
  }

  const handleClose = (event: Event) => {
    event.stopPropagation()
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return
    }
    if (isSelectionLoading) {
      return
    }

    updateOpen(false)
  }

  return (
    <>
      <Tooltip title={tooltipTitle}>
        <div
          ref={anchorRef}
          aria-label='split button'
          className={`h-9 ${fullWidth ? 'w-full' : 'w-fit'} ${
            size === 'minimal' ? 'flex justify-center items-center' : ''
          }  ${disabled ? 'cursor-not-allowed' : ''}`}
        >
          {size === 'minimal' ? (
            <button className='flex items-center justify-bottom' onClick={e => handleToggle(e)}>
              <div className='flex items-center justify-bottom'>
                <span className='text-sm'>{options?.[selectedIndex]?.label}</span>
                <CaretDown className='size-4 opacity-50' />
              </div>
            </button>
          ) : (
            <TriggerButton
              variant={variant}
              color={variant !== 'contained' ? 'secondary' : 'primary'}
              aria-controls={open ? 'split-button-menu' : undefined}
              aria-expanded={open ? 'true' : undefined}
              aria-label='Select create mode'
              aria-haspopup='menu'
              fullWidth={fullWidth}
              onClick={handleToggle}
              className={`flex gap-1 items-center text-sm justify-between shadow-sm !border-2 !border-solid !border-gray-light bg-white ${
                size === 'small' ? 'p-2 h-8' : 'p-3 h-9'
              } ${disabled ? 'disabled' : ''}`}
              disabled={disabled}
            >
              {title && (
                <span className={`font-normal  ${!disabled ? 'text-[#4d4d4d]' : 'text-gray-dark'}`}>{title}</span>
              )}
              <span
                className={`text-coal-dark  ${size === 'small' ? 'text-text-xs-medium' : 'text-text-sm-medium'} ${
                  !disabled ? 'text-[#4d4d4d]' : 'text-gray-dark'
                }`}
              >
                {' '}
                {variant === 'contained' ? (
                  <>
                    {renderOnlyIcon && options?.[0]?.icon && React.createElement(options?.[0]?.icon)}{' '}
                    {!hideLabel && options?.[0]?.label}
                  </>
                ) : (
                  <>
                    {renderOnlyIcon &&
                      options?.[selectedIndex]?.icon &&
                      React.createElement(options?.[selectedIndex]?.icon)}{' '}
                    {!hideLabel && options?.[selectedIndex]?.label}
                  </>
                )}{' '}
              </span>
              {!hideIcon && <ChevronDown className='w-4 h-4 text-coal-dark ml-auto' />}
            </TriggerButton>
          )}
        </div>
      </Tooltip>
      <Popper
        sx={{
          zIndex: 99990
        }}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        placement={popperConfig?.placement ?? 'bottom-end'}
        disablePortal={disablePortal ?? true}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              minWidth: '100px',
              width: '100%',
              transformOrigin: placement === 'bottom' ? 'right end' : 'right start'
            }}
          >
            <Paper sx={{ borderRadius: 2, maxHeight: popperConfig?.maxHeight, overflow: 'auto' }}>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id='split-button-menu' autoFocusItem>
                  {options?.map((option: any, index: number) => {
                    const isItemLoading = isSelectionLoading && pendingIndex === index
                    const iconContent = isItemLoading ? (
                      <Loader style={{ height: 12, width: 12, strokeWidth: 3 }} className='animate-spin' />
                    ) : (
                      option.icon && <option.icon />
                    )

                    return (
                      <Permission
                        permissionId={option.permissionId}
                        key={`${index}_${option.permissionId}`}
                        customClass='flex justify-center items-center w-full'
                      >
                        <MenuItem
                          disabled={option?.disabled || (isSelectionLoading && pendingIndex !== index)}
                          key={option?.value}
                          selected={index === selectedIndex}
                          className={iconSlotClassName ? 'flex justify-between items-center w-full' : undefined}
                          onClick={event => {
                            event.stopPropagation()
                            handleMenuItemClick(event, index, option?.value)
                          }}
                          sx={
                            renderOnlyIcon
                              ? {
                                  justifyContent: 'center'
                                }
                              : {}
                          }
                        >
                          {iconSlotClassName ? (
                            <>
                              <p className='text-text-sm-medium flex-1'>{option.label}</p>
                              <span className={iconSlotClassName}>{iconContent}</span>
                            </>
                          ) : (
                            <>
                              {iconContent}
                              <p className='text-text-sm-medium'>{option.label}</p>
                            </>
                          )}
                        </MenuItem>
                      </Permission>
                    )
                  })}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  )
}

export default DropdownButton
